import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useTable from '@hook/shared/use-table'

import TableGridConfigDefault from '@component/Layout/TableGridPersonalized/Body/Optimizer/item-default'

import TableGridItemAlertFollowUp from './alert/item-followup'
import TableGridItemAlertPurchase from './alert/item-purchase'
import TableGridCommecial from './budget/item-commercial'
import TableGridConciliation from './budget/item-conciliation'
import TableGridParameter from './budget/item-parameters'
import TableGridParameterMarge from './budget/item-parameters-marge'
import TableGridStore from './budget/item-store'
import TableGridItem from './Item'
import TableGridItemExportFooter from './Item-export-footer'
import { TableGridItemKpiLogistic } from './kpi/item-logistic'
import { TableGridItemKpiRupture } from './kpi/item-rupture'
import { TableGridItemKpiSales } from './kpi/item-sales'
import { TableGridItemKpiStorage } from './kpi/item-storage'
import TableGridDistribution from './mrp/item-distribution'
import TableGridOportunity from './mrp/item-oportunity'
import TableGridPurshase from './mrp/item-purshance'
import TableGridReabMrp from './mrp/item-reab'
import { TableGridItemOtbFollowUp } from './otb/item-follow'
import { TableGridItemOtbPlan } from './otb/item-plan'
import TableGridItemCronPurchaseUser from './parametrization/item-cron-user'
import TableGridParametrizationIncrementItem from './parametrization/item-increment'
import TableGridItemParametrizationReabRoute from './parametrization/item-reab-route'
import TableGridItemParametrizationRevision from './parametrization/item-revision'
import TableGridParametrizationSupplyItem from './parametrization/item-supply'

interface ITableGridBody {
  undoValue?: number
  updateIsGraphic?: (data: any) => void
  updatedFinalStorage?: (value: string, data: any) => void
}

const MRP_ENTITIES = new Set([
  'mrp-purchase',
  'mrp-distribution',
  'mrp-oportunity',
  'mrp-reab'
])

const CHUNK_SIZE = 50

const TableGridBody: FC<ITableGridBody> = ({
  undoValue,
  updateIsGraphic,
  updatedFinalStorage
}) => {
  const { data: rows, columns, actions, entity, context } = useTable()
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE)
  const sentinelRef = useRef<HTMLTableRowElement>(null)

  const allRows = rows.data
  const isMrp = MRP_ENTITIES.has(entity)

  // Reset visible count when data changes (new sort, search, page)
  useEffect(() => {
    setVisibleCount(CHUNK_SIZE)
  }, [allRows.length])

  // Infinite scroll observer — only for MRP entities
  useEffect(() => {
    if (!isMrp || visibleCount >= allRows.length) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(prev => Math.min(prev + CHUNK_SIZE, allRows.length))
        }
      },
      { threshold: 0.1 }
    )

    const el = sentinelRef.current
    if (el) observer.observe(el)

    return () => {
      if (el) observer.unobserve(el)
    }
  }, [isMrp, visibleCount, allRows.length])

  const columnsCount = useMemo(() => {
    let count = 0

    for (const column of columns) {
      if (column.columns) {
        count += column.columns?.length
      } else {
        count += 1
      }
    }
    if (actions && actions.length > 0) {
      count++
    }
    return count
  }, [columns, actions])

  // For MRP: slice to visible count. For others: show all.
  const displayRows = isMrp ? allRows.slice(0, visibleCount) : allRows
  const hasMore = isMrp && visibleCount < allRows.length

  const renderRow = useCallback(
    (row: any, key: number) => {
      if (entity === 'parametrization-revision') {
        return (
          <TableGridItemParametrizationRevision
            data={row}
            key={`table-grid-${key + 1}`}
          />
        )
      } else if (entity === 'config-default') {
        return (
          <TableGridConfigDefault
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'mrp-purchase') {
        return (
          <TableGridPurshase
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            onUpdateWanted={context?.onUpdateWanted}
          />
        )
      } else if (entity === 'parametrization-supply') {
        return (
          <TableGridParametrizationSupplyItem
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'parametrization-increment') {
        return (
          <TableGridParametrizationIncrementItem
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'mrp-distribution') {
        return (
          <TableGridDistribution
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'alert-purchase') {
        return (
          <TableGridItemAlertPurchase
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'mrp-oportunity') {
        return (
          <TableGridOportunity
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'mrp-reab') {
        return (
          <TableGridReabMrp
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'alert-followUp') {
        return (
          <TableGridItemAlertFollowUp
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'budget-conciliation') {
        return (
          <TableGridConciliation
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'kpi-sales' || entity === 'kpi-sales-modal') {
        return (
          <TableGridItemKpiSales
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            updateIsGraphic={updateIsGraphic}
          />
        )
      } else if (entity === 'kpi-storage' || entity === 'kpi-storage-modal') {
        return (
          <TableGridItemKpiStorage
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            updateIsGraphic={updateIsGraphic}
          />
        )
      } else if (entity === 'kpi-logistic' || entity === 'kpi-logistic-modal') {
        return (
          <TableGridItemKpiLogistic
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            updateIsGraphic={updateIsGraphic}
          />
        )
      } else if (entity === 'otb-followUp') {
        return (
          <TableGridItemOtbFollowUp
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            updateIsGraphic={updateIsGraphic}
          />
        )
      } else if (entity === 'kpi-rupture' || entity === 'kpi-rupture-modal') {
        return (
          <TableGridItemKpiRupture
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            updateIsGraphic={updateIsGraphic}
          />
        )
      } else if (entity === 'budget-commercial') {
        return (
          <TableGridCommecial
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'budget-store') {
        return (
          <TableGridStore
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'budget-parameter') {
        return (
          <TableGridParameter
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'cron-purchase-user') {
        return (
          <TableGridItemCronPurchaseUser
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'reab-routes') {
        return (
          <TableGridItemParametrizationReabRoute
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'budget-parameter-marge') {
        return (
          <TableGridParameterMarge
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
          />
        )
      } else if (entity === 'otb-plan' || entity === 'otb-plan-modal') {
        return (
          <TableGridItemOtbPlan
            data={row}
            key={`table-grid-${key + 1}`}
            indexData={key}
            undoValue={undoValue}
            updatedFinalStorage={updatedFinalStorage}
          />
        )
      } else if (entity === 'vendas-footer') {
        return (
          <TableGridItemExportFooter
            data={row}
            key={`table-grid-${key + 1}`}
            isLast={key === allRows.length - 1}
          />
        )
      } else {
        return <TableGridItem data={row} key={`table-grid-${key + 1}`} />
      }
    },
    [
      entity,
      undoValue,
      updateIsGraphic,
      updatedFinalStorage,
      allRows.length,
      context
    ]
  )

  return (
    <tbody>
      {allRows.length === 0 ? (
        <tr>
          <td colSpan={columnsCount} align="center">
            Dados não encontrados
          </td>
        </tr>
      ) : (
        <>
          {displayRows.map((row, key) => renderRow(row, key))}
          {hasMore && (
            <tr ref={sentinelRef}>
              <td
                colSpan={columnsCount}
                align="center"
                style={{
                  padding: '12px',
                  color: '#718096',
                  fontSize: '13px'
                }}
              >
                Carregando mais registros... ({visibleCount} de {allRows.length}
                )
              </td>
            </tr>
          )}
        </>
      )}
    </tbody>
  )
}

export default TableGridBody
